import { SessionCoordinator, UploadClient, noRefreshStore } from '@micarahr/api-client';
import { env } from '../config/env';

/**
 * The web application's one connection to the API.
 *
 * A module-level singleton because the session it owns must be singular: two
 * coordinators would hold two access tokens and refresh independently, which is
 * the refresh storm the single-flight rule exists to prevent, reintroduced one
 * level up.
 *
 * The refresh credential is not stored here at all. The server puts it in an
 * HttpOnly cookie the browser will not let script read, so `noRefreshStore` is
 * accurate rather than a placeholder — there is genuinely nothing to keep.
 */

let coordinator: SessionCoordinator | null = null;
let uploads: UploadClient | null = null;

/**
 * Called when a session ends, so caches and sockets can be torn down. The
 * message is the server's, when it said why (an idle timeout).
 */
type SignOutHandler = (reason: string, message?: string) => void;
const signOutHandlers = new Set<SignOutHandler>();

export function onSessionEnded(handler: SignOutHandler): () => void {
  signOutHandlers.add(handler);
  return () => signOutHandlers.delete(handler);
}

export function apiSession(): SessionCoordinator {
  coordinator ??= new SessionCoordinator({
    baseUrl: env().apiBaseUrl,
    client: 'web',
    // Nothing to persist: the cookie is the store.
    store: noRefreshStore,
    defaultHeaders: () => ({
      'x-client': 'web',
      'x-app-version': env().appVersion,
    }),
    onSignOut: (reason: string, message?: string) => {
      for (const handler of signOutHandlers) handler(reason, message);
    },
  });

  return coordinator;
}

export function apiTransport() {
  return apiSession().transport;
}

export function apiUploads(): UploadClient {
  uploads ??= new UploadClient(apiTransport(), { maxBytes: env().uploadMaxBytes });
  return uploads;
}

/** Test seam, and used when the data source is switched in development. */
export function resetApiClient(): void {
  coordinator = null;
  uploads = null;
}
